import React, { ReactNode, useState } from 'react';
import { Box, Paper } from '@mui/material';
import Button from '@mui/material/Button';

import { cancelInsertion } from '../../../../store/actions/insertActions';
import { ConfirmCancel } from '../../../dialogs/ConfirmCancel';
import { useAppDispatch } from '../../../hooks';

interface Props {
  insertButtonText?: string;

  onInsert?: () => void;

  /**
   * If provided, will be used instead of the default cancel action of cancelling the insertion.
   */
  onCancel?: () => void;

  /**
   * If false, no confirm dialog will be shown (defaults to showing dialog).
   */
  confirmCancelation?: boolean;

  /**
   * If true, the Insert button will not be shown.
   */
  hideSubmitButton?: boolean;

  /**
   * If true, the Cancel button will not be shown.
   */
  hideCancelButton?: boolean;

  disabled?: boolean;

  /**
   * If true, the "extra buttons" provided as children will be rendered inline between the normal "Cancel" and "Edit"
   *   buttons, instead of separated and floated left which is the default.
   */
  inlineChildren?: boolean;

  children?: ReactNode | undefined;
}

export function InsertCancelBar(props: Props) {
  const dispatch = useAppDispatch();

  const [dialogOpen, setDialogOpen] = useState<boolean>(false);

  const onCancelConfirmed = () => {
    if (props.onCancel) {
      props.onCancel();
    } else {
      dispatch(cancelInsertion());
    }
  };

  return (
    <Paper elevation={1} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', py: 1 }}>
      {props.inlineChildren !== true && <Box>{props.children}</Box>}

      <Box sx={{ marginLeft: 'auto' }}>
        {props.hideCancelButton ? null : (
          <Button
            sx={{ m: 1, py: 1 }}
            variant="outlined"
            onClick={() => {
              if (props.confirmCancelation === false) {
                onCancelConfirmed();
              } else {
                setDialogOpen(true);
              }
            }}
          >
            Cancel
          </Button>
        )}
        {props.inlineChildren === true && props.children}
        {props.hideSubmitButton ? null : (
          <Button
            id="insert-button"
            disabled={props.disabled}
            sx={{ m: 1, py: 1 }}
            variant="contained"
            onClick={() => {
              if (typeof props.onInsert === 'function' && !props.disabled) {
                props.onInsert();
              }
            }}
          >
            {props.insertButtonText ?? 'Insert'}
          </Button>
        )}
      </Box>
      {dialogOpen ? (
        <ConfirmCancel
          onClose={(confirmed) => {
            setDialogOpen(false);

            if (confirmed) {
              onCancelConfirmed();
            }
          }}
        />
      ) : null}
    </Paper>
  );
}
